import { Fragment, useEffect, useRef, useState } from 'react';
import { activeOrganizationId, graphql } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { answerDateField, answerField, jobFields, type JobQuestion } from './job-questions';

/**
 * The questions about the job that a rule in its state turns on (New Mexico: how many units the property has), asked
 * as the customer fills in the job. They follow the state, type of job, role and who hired the customer as those are
 * chosen, so a job no rule asks anything of shows nothing here.
 */
export function JobQuestions({ initial, answers, dates }: { initial: Record<(typeof jobFields)[number], string>; answers?: Record<string, string> | undefined;
  dates?: Record<string, string> | undefined }) {
  const { token } = useAuth();
  const anchor = useRef<HTMLDivElement>(null);
  // The job's facts as the form first shows them, then as the customer changes them.
  const [facts, setFacts] = useState(() => JSON.stringify(Object.fromEntries(jobFields.map(field => [field, initial[field]]))));
  const [loaded, setLoaded] = useState<{ facts: string; questions: JobQuestion[] } | null>(null);
  // The answers as chosen, so a Yes whose event has a date asks for it.
  const [chosen, setChosen] = useState<Record<string, string>>(() => ({ ...answers }));
  const questions = loaded?.facts === facts ? loaded.questions : [];
  useEffect(() => {
    const form = anchor.current?.closest('form');
    if (!form) return;
    const read = () => setFacts(JSON.stringify(Object.fromEntries(jobFields.map(field => [field, String(new FormData(form).get(field) ?? '')]))));
    form.addEventListener('input', read); form.addEventListener('change', read);
    return () => { form.removeEventListener('input', read); form.removeEventListener('change', read); };
  }, []);
  useEffect(() => {
    if (!token || !facts) return;
    const job = JSON.parse(facts) as Record<(typeof jobFields)[number], string>;
    if (!/^[A-Za-z]{2}$/.test(job.stateCode) || !job.classification || !job.claimantRole || !job.projectUse) return;
    let current = true;
    void graphql<{ jobQuestions: JobQuestion[] }>({
      query: 'query JobQuestions($stateCode: String!, $classification: String!, $claimantRole: String!, $contractingTier: Int!, $projectUse: String!) { jobQuestions(stateCode: $stateCode, classification: $classification, claimantRole: $claimantRole, contractingTier: $contractingTier, projectUse: $projectUse) { key question help answers { value label } dateQuestion } }',
      variables: { ...job, contractingTier: Number(job.contractingTier) }, token, organizationId: activeOrganizationId() })
      .then(result => { if (current) setLoaded({ facts, questions: result.jobQuestions ?? [] }); })
      // Without the questions the job still saves; the notice asks for any answer its rule needs before it starts.
      .catch(() => { if (current) setLoaded({ facts, questions: [] }); });
    return () => { current = false; };
  }, [facts, token]);
  // Always rendered, so it can find its form; hidden, it adds no gap to the form's grid.
  return <div ref={anchor} className="full-field" hidden={!questions.length}>{questions.length > 0 && <fieldset><legend>About this job’s property</legend>
    <p className="form-hint full-field">The law in this state depends on {questions.length > 1 ? 'these answers' : 'this answer'}. If you are not sure, choose Not sure.</p>
    {questions.map(question => <Fragment key={question.key}><label>{question.question}
      <select required name={`${answerField}${question.key}`} defaultValue={answers?.[question.key] ?? ''}
        onChange={event => { const value = event.currentTarget.value; setChosen(current => ({ ...current, [question.key]: value })); }}>
        <option value="" disabled>Choose an answer</option>
        {question.answers.map(answer => <option key={answer.value} value={answer.value}>{answer.label}</option>)}
      </select>{question.help && <small>{question.help}</small>}</label>
      {question.dateQuestion && chosen[question.key] === 'YES' && <label>{question.dateQuestion}
        <input type="date" name={`${answerDateField}${question.key}`} defaultValue={dates?.[question.key] ?? ''} />
        <small>A notice’s due date may be counted from this day. Leave it empty if you don’t know it yet. If a notice needs it, the job says so.</small></label>}</Fragment>)}
  </fieldset>}</div>;
}
